import { useState } from "react";
import { Check, X, Radar, Loader2 } from "lucide-react";

const Row = ({ ok, label, note, testId }) => (
  <div className="flex items-start gap-3 py-3 border-b border-white/5" data-testid={testId} data-ok={ok ? "yes" : "no"}>
    <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${ok ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-white/5 text-white/30"}`}>{ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}</span>
    <div><div className="font-bold text-sm">{label}</div><div className="text-xs text-white/50">{note}</div></div>
  </div>
);

const PLATFORM = { ios: "iPhone / iPad", android: "Android", windows: "Windows", mac: "Mac", chromeos: "ChromeOS", other: "this device" };

export default function CompatPanel({ caps }) {
  const [scan, setScan] = useState(null);
  const discover = async () => {
    setScan({ busy: true });
    const out = { busy: false, cast: null, remote: null };
    try {
      if (caps.presentation) {
        const req = new window.PresentationRequest([`${window.location.origin}/mirror/tv`]);
        const av = await Promise.race([req.getAvailability(), new Promise((_, r) => setTimeout(r, 4000))]);
        out.cast = av.value;
      }
    } catch { out.cast = null; }
    try {
      if (caps.remote) {
        const v = document.createElement("video"); v.src = "data:video/mp4;base64,AAAAIGZ0eXBpc29t";
        out.remote = await Promise.race([new Promise((res) => v.remote.watchAvailability((a) => res(a)).catch(() => res(null))), new Promise((res) => setTimeout(() => res(null), 4000))]);
      }
    } catch { out.remote = null; }
    setScan(out);
  };
  const p = caps.platform;
  return (
    <div className="space-y-6" data-testid="mirror-compat">
      <section className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
        <div className="mono text-[10px] uppercase tracking-widest text-white/40">Your device</div>
        <div className="font-heading font-black text-2xl mt-1" data-testid="mirror-compat-device">{caps.browser} on {PLATFORM[p]}</div>
        <div className="mt-3">
          <Row ok={caps.webrtc} label="Jixxeo TV pairing" note="Stream to any TV browser with a 6-digit code." testId="mirror-cap-webrtc" />
          <Row ok={caps.screen} label="Screen & tab mirroring" note={caps.screen ? "Share your whole screen, a window or one tab." : p === "ios" || p === "android" ? "Mobile browsers can't capture the screen. Use your phone's built-in Screen Mirroring / Smart View (see Guides)." : "This browser can't capture the screen — try Chrome, Edge or Firefox."} testId="mirror-cap-screen" />
          <Row ok={caps.capture} label="Stream video files to Jixxeo TV" note={caps.capture ? "Play local videos on the TV page." : "Not on this browser — use AirPlay/Cast for videos instead."} testId="mirror-cap-capture" />
          <Row ok={caps.presentation} label="Open on Chromecast" note={caps.presentation ? "Launch the Jixxeo TV page on a Chromecast from this browser." : "Only Chrome/Edge (desktop & Android) can do this."} testId="mirror-cap-presentation" />
          <Row ok={caps.remote} label="Cast a video with your device" note={caps.remote ? "Video player Cast button (Remote Playback)." : "Remote Playback isn't available here."} testId="mirror-cap-remote" />
          <Row ok={caps.airplay} label="AirPlay" note={caps.airplay ? "Safari can AirPlay videos to Apple TV & AirPlay 2 TVs." : "AirPlay buttons only exist in Safari on Apple devices."} testId="mirror-cap-airplay" />
          <Row ok={false} label="Miracast" note="No browser can start Miracast. Use Windows key + K or Android Smart View — steps in Guides." testId="mirror-cap-miracast" />
        </div>
      </section>
      <section className="rounded-3xl border border-[#00F0FF]/20 bg-[#00F0FF]/5 p-5">
        <div className="flex items-center gap-3">
          <Radar className="w-6 h-6 text-[#00F0FF]" />
          <div className="flex-1"><div className="font-bold">Look for TVs nearby</div><div className="text-xs text-white/50">Browsers only report whether a cast-capable TV is reachable — they never share a device list.</div></div>
          <button onClick={discover} disabled={scan?.busy} data-testid="mirror-discover-btn" className="bg-[#00F0FF] text-black font-black rounded-full px-4 py-2 text-sm">{scan?.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Scan"}</button>
        </div>
        {scan && !scan.busy && (
          <div className="mt-4 text-sm space-y-1" data-testid="mirror-discover-result">
            <div>Chromecast / Google Cast: <b>{scan.cast === true ? "TV found ✓" : scan.cast === false ? "none found" : "can't check in this browser"}</b></div>
            <div>Device Cast / AirPlay for videos: <b>{scan.remote === true ? "TV found ✓" : scan.remote === false ? "none found" : "can't check in this browser"}</b></div>
            <div className="text-xs text-white/50 pt-1">Not listed? Any smart TV with a browser works with the Jixxeo TV page and a code.</div>
          </div>
        )}
      </section>
    </div>
  );
}
